import { Box, Flex } from '@chakra-ui/react';
import { Tags, Text } from '@kula-ai/components';
import { Mail01 } from '@kula-ai/icons-core';
import type { FieldRowProps, JobPostingTabProps } from './JobPostingTab.types';

const formatDestinations = (destinations: string[]): string => {
  if (destinations.length === 0) return 'Not listed yet.';
  if (destinations.length === 1) return `Listed in ${destinations[0]}.`;
  if (destinations.length === 2) {
    return `Listed in ${destinations[0]} and ${destinations[1]}.`;
  }
  const head = destinations.slice(0, -1).join(', ');
  const tail = destinations[destinations.length - 1];
  return `Listed in ${head} and ${tail}.`;
};

const FieldRow = ({ field }: FieldRowProps) => (
  <Flex
    align="center"
    justify="space-between"
    px="12px"
    py="10px"
    border="1px solid"
    borderColor="border-color"
    borderRadius="6px"
  >
    <Text variant="span" className="text-secondary-700">
      {field.label}
    </Text>
    {field.mandatory && <Tags label="Mandatory" />}
  </Flex>
);

export const JobPostingTab = ({ data }: JobPostingTabProps) => (
  <Flex direction="column" gap="14px">
    <Box>
      <Text variant="h4" className="text-secondary-900 block mb-2">
        {data.title}
      </Text>
      <Text variant="small" className="text-secondary-700">
        {formatDestinations(data.destinations)}
      </Text>
    </Box>

    <Box
      bg="secondary-50"
      borderRadius="8px"
      px="12px"
      py="6px"
      alignSelf="flex-start"
    >
      <Text variant="small" className="text-secondary-700 font-medium">
        Application form
      </Text>
    </Box>

    <Flex
      align="flex-start"
      gap="10px"
      px="12px"
      py="10px"
      border="1px solid"
      borderColor="border-color"
      borderRadius="8px"
    >
      <Box color="secondary-700" mt="2px">
        <Mail01 size={14} color="currentColor" />
      </Box>
      <Box>
        <Text variant="span" className="text-secondary-900 font-medium block">
          {data.hasSubmissionEmail
            ? 'Submission email enabled'
            : 'No application submission email'}
        </Text>
        <Text variant="small" className="text-secondary-400">
          Send an email after candidates submit the application form.
        </Text>
      </Box>
    </Flex>

    <Box>
      <Text variant="small" className="text-secondary-400 block mb-2">
        {data.applicationForm.name}
      </Text>
      <Text
        variant="small"
        className="text-secondary-400 uppercase tracking-wide block mb-2"
      >
        Personal information
      </Text>
      <Flex direction="column" gap="6px">
        {data.applicationForm.fields.map((field) => (
          <FieldRow key={field.id} field={field} />
        ))}
      </Flex>
    </Box>
  </Flex>
);
